import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { PlusIcon } from '@/app/common/icons/arrows'
import { parseRequestError } from '@/app/common/utilities'
import { usePostProgramCatalogCategory } from '@/queries/program_catalog/hooks/usePostProgramCatalogCategory'
import { SystemCatalogCategory } from '@/queries/system_catalog/system_catalog.schema'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import * as z from 'zod'

// Define the form schema with Zod
const formSchema = z.object({
  serviceFee: z
    .string()
    .min(1, { message: 'Service Fee is required' })
    .refine((val) => !isNaN(parseFloat(val)), {
      message: 'Service Fee must be a number',
    }),
})

// Type for form values
type FormValues = z.infer<typeof formSchema>

interface Props {
  category: SystemCatalogCategory
  onAddCategorySuccess: () => void
}

function AddCategoryPopup(props: Props) {
  const { category, onAddCategorySuccess } = props

  // Dialog state
  const [isOpen, setIsOpen] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serviceFee: '',
    },
  })
  const { toast } = useToast()

  const programId = useSelector(selectSelectedProgram).id
  const addCategoryMutation = usePostProgramCatalogCategory(programId, {
    onSuccess: () => {
      onAddCategorySuccess()
      toast({
        variant: 'default',
        title: 'Item was succesfully added',
        description: 'The item was added to the program catalog.',
      })
      setIsOpen(false)
      reset()
    },
    onError: (error) => {
      const parsedError = parseRequestError(error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: parsedError.message,
      })
    },
  })

  // Handle dialog open state changes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      reset()
    }
  }

  // Form submission handler
  const onSubmit = (values: FormValues) => {
    addCategoryMutation.mutate({
      apply_service_fee_to_items: 0,
      global_item_category_id: category.id,
      program_items: [],
      service_fee: parseFloat(values.serviceFee),
    })
  }

  // Custom handler for service fee input to only allow numeric values
  const handleServiceFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Allow empty string, digits, and decimal point
    if (value === '' || /^(\d+\.?\d*|\.\d+)$/.test(value)) {
      e.target.value = value
    } else {
      e.preventDefault()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="flex items-center"
      >
        Add Category
        <PlusIcon className="ml-2 text-xs" />
      </Button>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Category to Program</DialogTitle>
          <p className="text-sm text-gray-600">
            Add {category.name} to your program and all of its items.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-4 pb-1">
          <div className="space-y-2">
            <Label htmlFor="serviceFee">
              Service Fee (%)<span className="text-red-500">*</span>
            </Label>
            <Input
              id="serviceFee"
              {...register('serviceFee')}
              type="number"
              step={0.01}
              placeholder="Enter service fee percentage"
              className={errors.serviceFee ? 'border-red-500' : ''}
              onChange={handleServiceFeeChange}
            />
            {errors.serviceFee && (
              <p className="text-red-500 text-sm">
                {errors.serviceFee.message}
              </p>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                status={addCategoryMutation.status}
                buttonTxt={'Add to Program'}
              />
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddCategoryPopup
